import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {Mail, Chrome} from 'lucide-react'

export default function AuthGateway() {
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  // Check if user is already authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (lumi.auth.isAuthenticated && lumi.auth.user) {
        await handlePostLoginRedirect(lumi.auth.user.userId)
      }
    }
    checkAuth()
  }, [])

  const handlePostLoginRedirect = async (userId: string) => {
    try {
      const user = lumi.auth.user
      if (!user) {
        navigate("/auth")
        return
      }

      // Check if user has selected a role
      const userProfile = await lumi.entities.UserProfiles.list({
        filter: { userId }
      })
      
      if (userProfile.list.length === 0) {
        // No role selected, redirect to role selection (auto-assignment happens there)
        navigate("/onboarding/role")
      } else {
        const profile = userProfile.list[0]
        // Direct routing based on role (skip profile stepper)
        const roleRoutes: Record<string, string> = {
          nurse: "/dashboard/nurse",
          recruiter: "/dashboard/recruiter",
          affiliate: "/dashboard/affiliate",
          admin: "/dashboard/admin",
          admin_regional: "/dashboard/admin",
          superadmin: "/dashboard/superadmin"
        }
        navigate(roleRoutes[profile.role] || "/dashboard/nurse")
      }
    } catch (error) {
      console.error("Error checking user profile:", error)
      navigate("/onboarding/role")
    }
  }

  const handleSignIn = async () => {
    setIsLoading(true)
    try {
      const result = await lumi.auth.signIn()
      if (result?.user) {
        toast.success("Welcome back!")
        await handlePostLoginRedirect(result.user.userId)
      }
    } catch (error) {
      toast.error("Authentication failed. Please try again.")
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <img 
            src="https://i.imghippo.com/files/KBd1355nw.png"
            alt="Nursing Hive"
            className="w-20 h-auto mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Sign in or Create your account
          </h1>
          <p className="text-gray-600">
            Choose your preferred sign-in method
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleSignIn}
            disabled={isLoading}
            className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <Mail className="w-5 h-5 mr-2" />
            {isLoading ? "Signing in..." : "Continue with Email & Password"}
          </button>

          <button
            onClick={handleSignIn}
            disabled={isLoading}
            className="w-full bg-white border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <Chrome className="w-5 h-5 mr-2" />
            Continue with Google
          </button>
        </div>

        <p className="text-sm text-gray-500 text-center mt-6">
          By signing in, you agree to our Privacy Policy and Terms of Service.
        </p>
      </div>
    </div>
  )
}
